import { useCallback, useContext, useRef, useState } from 'react'
import { FiDownload } from 'react-icons/fi'

import { FormHandles } from '@unform/core'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import FileDownload from 'js-file-download'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import { FormGroup, Input, Label } from '~/components/unform'
import Form from '~/components/unform/Form'
import Select from '~/components/unform/ReactSelect'
import { channelOptions } from '~/modules/retailMedia/pages/external-campaign-metrics/constants'
import { ExternalCampaignMetricsService } from '~/modules/retailMedia/services/metrics'

type ExportFormData = {
  start_date: string
  end_date: string
  channel: string
  placement_name: string
}

const allChannelsOption = {
  value: '',
  label: t('rm:externalCampaignMetrics.export.channelPlaceholder')
}
const channelOptionsWithAll = [allChannelsOption, ...channelOptions]

const ExportSection = () => {
  const { toast } = useContext(FeedbackContext)
  const formRef = useRef<FormHandles>(null)
  const [loading, setLoading] = useState(false)

  const handleExport = useCallback(async () => {
    const data = formRef.current?.getData() as ExportFormData

    if (!data?.start_date || !data?.end_date) {
      toast.warning(t('rm:externalCampaignMetrics.export.dateRequired'))
      return
    }

    try {
      setLoading(true)

      const params: {
        start_date: string
        end_date: string
        channel?: string
        placement_name?: string
      } = {
        start_date: data.start_date,
        end_date: data.end_date
      }

      if (data.channel) params.channel = data.channel
      if (data.placement_name) params.placement_name = data.placement_name

      const response =
        await ExternalCampaignMetricsService.exportMetrics(params)

      const contentDisposition = response.headers['content-disposition']
      const fileNameMatch = /filename\*?=(?:UTF-8'')?([^;\s]+)/.exec(
        contentDisposition || ''
      )
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1])
        : `Relatorio de metricas externas - ${data.start_date} a ${data.end_date}.xlsx`

      FileDownload(response.data, fileName)
    } catch (error) {
      const axiosError = error as AxiosError

      if (axiosError.response?.status === 404) {
        toast.warning(t('rm:externalCampaignMetrics.export.noData'))
      } else {
        toast.critical(t('rm:externalCampaignMetrics.export.error'))
      }
    } finally {
      setLoading(false)
    }
  }, [toast])

  return (
    <Form ref={formRef} onSubmit={handleExport}>
      <section className="border border-1 border-neutral-high-dark rounded p-3 mb-4">
        <p className="fw-bold mb-3">
          {t('rm:externalCampaignMetrics.export.title')}
        </p>
        <div className="row">
          <div className="col-auto">
            <FormGroup>
              <Label
                htmlFor="start_date"
                text={t('rm:externalCampaignMetrics.export.startDate')}
                isRequired
              />
              <Input name="start_date" type="date" id="start_date" />
            </FormGroup>
          </div>

          <div className="col-auto">
            <FormGroup>
              <Label
                htmlFor="end_date"
                text={t('rm:externalCampaignMetrics.export.endDate')}
                isRequired
              />
              <Input name="end_date" type="date" id="end_date" />
            </FormGroup>
          </div>

          <div className="col-auto">
            <FormGroup>
              <Label
                htmlFor="channel"
                text={t('rm:externalCampaignMetrics.export.channel')}
              />
              <Select
                name="channel"
                options={channelOptionsWithAll}
                defaultValue={allChannelsOption}
              />
            </FormGroup>
          </div>

          <div className="col-auto">
            <FormGroup>
              <Label
                htmlFor="placement_name"
                text={t('rm:externalCampaignMetrics.export.placementName')}
              />
              <Input
                name="placement_name"
                id="placement_name"
                placeholder={t(
                  'rm:externalCampaignMetrics.export.placementPlaceholder'
                )}
              />
            </FormGroup>
          </div>

          <div className="col-auto d-flex align-items-end">
            <FormGroup>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                template="primary"
                iconLeft={<FiDownload />}
                text={t('rm:externalCampaignMetrics.export.button')}
                textLoading={t('rm:externalCampaignMetrics.export.exporting')}
                customWidth="auto"
              />
            </FormGroup>
          </div>
        </div>
      </section>
    </Form>
  )
}

export default ExportSection
